// @flow
import history from '../history';
import authService from '../services/auth-service';
import userActions from './user';
import { hideIntercom, bootIntercom } from '../services/intercom-service';

import type { AuthState, FormError } from '../store/reducers/auth';
import { getRouteTFunction } from '../i18n';

type FormAction = {
  email?: string,
  password?: string,
  tfa_token?: string
}

type ApiError = {
  code?: string,
  message: string
}

type AuthFormAction = {
  type: 'SET_AUTH_FORM',
} & FormAction;

export function setAuthForm(action: FormAction): AuthFormAction {
  return {
    type: 'SET_AUTH_FORM',
    ...action
  };
}

type AuthenticateAction = { type: 'AUTHENTICATE' };

export function authenticate(): AuthenticateAction {
  return {
    type: 'AUTHENTICATE'
  };
}

type UnAuthenticateAction = {
  type: 'AUTHENTICATION_FAILURE',
  error?: ApiError[]
};

export function authenticationFailure(error?: ApiError[]): UnAuthenticateAction {
  return {
    type: 'AUTHENTICATION_FAILURE',
    error: error || []
  };
}

type ReLoadAuthAction = { type: 'RELOAD_AUTH' };

export function reLoadAuth(): ReLoadAuthAction {
  return {
    type: 'RELOAD_AUTH'
  };
}

type LoadAuthAction = { type: 'LOAD_AUTH' };

export function loadAuth(): LoadAuthAction {
  return {
    type: 'LOAD_AUTH'
  };
}

type SetFormErrorsAction = {
  type: 'AUTH_FORM_ERRORS',
  errors: FormError[]
}

export function setFormErrors(errors: FormError[]): SetFormErrorsAction {
  return {
    type: 'AUTH_FORM_ERRORS',
    errors
  };
}

type TfaChallengeAction = { type: 'TFA_CHALLENGE' };

export function setTfaChallenge(): TfaChallengeAction {
  return { type: 'TFA_CHALLENGE' };
}

type TfaTokenAction = {
  type: 'SET_TFA_TOKEN',
  token: string
}

export function setTfaToken(token: string): TfaTokenAction {
  return { type: 'SET_TFA_TOKEN', token };
}

type LoginMessageAction = {
  type: 'LOGIN_MESSAGE',
  message: string,
  code: string
}

function setMessage(message: string, code: string): LoginMessageAction {
  return {
    type: 'LOGIN_MESSAGE',
    message,
    code
  };
}

type AuthSuccessAction = {
  type: 'AUTH_SUCCESS'
}

function authSuccess(): AuthSuccessAction {
  return {
    type: 'AUTH_SUCCESS'
  };
}

type LogoutAction = {
  type: 'LOGOUT';
}

function logoutAction(): LogoutAction {
  return { type: 'LOGOUT' };
}

type ReturningAction = {
  type: 'RETURNING';
}

function returning(): ReturningAction {
  return { type: 'RETURNING' };
}

export type Action = AuthFormAction |
  AuthenticateAction |
  LoadAuthAction |
  SetFormErrorsAction |
  TfaChallengeAction |
  TfaTokenAction |
  UnAuthenticateAction |
  AuthSuccessAction |
  LogoutAction |
  ReturningAction |
  LoginMessageAction;
type PromiseAction = Promise<Action>;
type Dispatch = (action: Action | ThunkAction | PromiseAction) => any;
type GetState = () => {
  auth: AuthState,
  user: any
};
type ThunkAction = (dispatch: Dispatch, getState: GetState) => any;

function scheduleTimeout(): ThunkAction {
  return ((dispatch) => {
    clearTimeout(window.tokenRenewalTimeout);
    const expiresAt = localStorage.getItem('expires_at');
    if (expiresAt) {
      const delay = JSON.parse(expiresAt) - Date.now() - 60000;
      if (delay > 0) {
        window.tokenRenewalTimeout = setTimeout(() => {
          dispatch(logout());
        }, delay);
      }
    }
  });
}

export function unsetError(field: string): ThunkAction {
  return ((dispatch, getState) => {
    const { auth } = getState();
    const errors = auth.formErrors.slice();
    const index = errors.findIndex((e) => e.field === field);
    if (index >= 0) {
      errors.splice(index, 1);
    }
    dispatch(setFormErrors(errors));
  });
}

export function signup(email: string, password: string): ThunkAction {
  return ((dispatch) => {
    dispatch(loadAuth());
    return authService.signup(email, password)
      .then(() => {
        authService.requestVerifyLink().then(() => {
          dispatch(authenticate());
          dispatch(scheduleTimeout());
          dispatch(userActions.createUser(email));
        });
      })
      .catch((error) => dispatch(setFormErrors(error)));
  });
}

export function login(email: string, password: string, tfaToken: ?string, remember: boolean): ThunkAction {
  return ((dispatch) => {
    dispatch(loadAuth());
    authService.login(email, password, tfaToken, remember)
      .then((data) => {
        if (data.access_token) {
          dispatch(authenticate());
          dispatch(scheduleTimeout());
          dispatch(userActions.fetchUser(['onboarding_state', 'address', 'region']));
          history.push('/');
        } else if (data.tfa_enabled) {
          dispatch(setTfaChallenge());
        }
      })
      .catch((error) => {
        if (error && error.length > 0 && (error[0].code === '2fa_required')) {
          dispatch(setTfaChallenge());
        } else {
          dispatch(setFormErrors(error));
        }
      });
  });
}

export function loadLocalStorageToStore(): ThunkAction {
  return (dispatch) => {
    dispatch(loadAuth());
    setTimeout(() => {
      if (authService.loggedIn) {
        dispatch(authenticate());
        dispatch(scheduleTimeout());
      } else if (authService.returning) {
        dispatch(returning());
      } else {
        dispatch(authenticationFailure());
      }
    });
  };
}

export function routeSession(): ThunkAction {
  return (dispatch, getState) => {
    const { auth } = getState();
    const routesTFunction = getRouteTFunction();

    if (auth.authenticated) {
      dispatch(userActions.fetchUser(['onboarding_state', 'address', 'region']));
    } else {
      bootIntercom();
      if (auth.returning) {
        history.push(routesTFunction('redirectPaths./login'));
      } else {
        history.push(routesTFunction('redirectPaths./signup'));
      }
    }
  };
}

export function requestVerifyEmail(): ThunkAction {
  return (dispatch) => {
    dispatch(loadAuth());
    authService.requestVerifyLink().then((response: { message: string }) => {
      dispatch(setMessage(response.message));
    });
  };
}

export function verifyEmail(token: string): ThunkAction {
  return (dispatch) => {
    dispatch(loadAuth());
    return authService.verifyEmail(token).then(() => {
      dispatch(setMessage('Thank you for verifying your email address', 'thank_you_for_verifying'));
    }).catch((error) => {
      dispatch(authenticationFailure([error]));
    });
  };
}

export function resetPassword(email: string): ThunkAction {
  return (dispatch) => {
    dispatch(loadAuth());
    return authService.resetPassword(email).then(() => {
      dispatch(setMessage(`An email has been sent to ${email}.
        This message will contain instructions for resetting your password.`, 'reset_email_sent'));
    })
      .catch((error) => {
        dispatch(authenticationFailure(error));
      });
  };
}

export function updatePassword(password: string, token: string, query: string): ThunkAction {
  const [, email] = query.split('=');
  return (dispatch) => {
    const routesTFunction = getRouteTFunction();
    dispatch(loadAuth());
    return authService.updatePassword(email, password, token).then(() => {
      history.push(routesTFunction('redirectPaths./login'));
    }).catch((error) => {
      dispatch(setFormErrors(error));
    });
  };
}

export function validatePasswordResetLink(token: string, query: string): ThunkAction {
  return (dispatch) => {
    dispatch(loadAuth());
    return authService.validatePasswordReset(token, query).then(() => {
      dispatch(authSuccess());
    }).catch((error) => {
      dispatch(authenticationFailure(error));
    });
  };
}

export function logout(): ThunkAction {
  return (dispatch) => {
    clearTimeout(window.tokenRenewalTimeout);
    const routesTFunction = getRouteTFunction();
    authService.logout().then(() => {
      dispatch(logoutAction());
      hideIntercom();
      history.push(routesTFunction('redirectPaths./login'));
      bootIntercom();
    });
  };
}

export function checkAuth(): ThunkAction {
  return (dispatch) => {
    if (authService.loggedIn) {
      dispatch(scheduleTimeout());
      return dispatch(authSuccess());
    }
    return dispatch(logout());
  };
}



// WEBPACK FOOTER //
// ./src/actions/auth.js